import React, { useState, useEffect } from 'react';
import { FaUsers, FaUserPlus, FaMoneyBill, FaClock, FaMale, FaFemale, FaHeart, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalUsers: 0,
        newUsersThisMonth: 0,
        paidUsers: 0,
        pendingPayments: 0,
        recentUsers: [],
        genderStats: [],
        maritalStats: []
    });

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get('https://app.bihebari.com.np/api/admin/dashboard', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            toast.error('Error loading dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        { title: 'Total Users', value: stats.totalUsers, icon: FaUsers, color: 'bg-blue-500' },
        { title: 'New Users (This Month)', value: stats.newUsersThisMonth, icon: FaUserPlus, color: 'bg-green-500' },
        { title: 'Paid Users', value: stats.paidUsers, icon: FaMoneyBill, color: 'bg-purple-500' },
        { title: 'Pending Payments', value: stats.pendingPayments, icon: FaClock, color: 'bg-yellow-500' }
    ];

    const genderChartData = {
        labels: stats.genderStats.map(item => item.gender),
        datasets: [{
            data: stats.genderStats.map(item => item.count),
            backgroundColor: ['#ef4444', '#3b82f6', '#a855f7'],
            borderWidth: 0
        }]
    };

    const maritalChartData = {
        labels: stats.maritalStats.map(item => item.marital),
        datasets: [{
            label: 'Number of Users',
            data: stats.maritalStats.map(item => item.count),
            backgroundColor: '#3b82f6',
            borderRadius: 8
        }]
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="text-center">
                    <FaSpinner className="text-4xl text-indigo-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard Overview</h1>
            
            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statCards.map((card, index) => {
                    const Icon = card.icon;
                    return (
                        <div key={index} className="bg-white rounded-xl shadow-md p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm">{card.title}</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-1">{card.value}</p>
                                </div>
                                <div className={`${card.color} p-3 rounded-full`}>
                                    <Icon className="text-white text-xl" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <FaHeart className="text-pink-500 mr-2" />
                        Gender Distribution
                    </h2>
                    <div className="h-64 flex items-center justify-center">
                        <Pie data={genderChartData} options={{ responsive: true, maintainAspectRatio: true }} />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Marital Status Distribution</h2>
                    <div className="h-64">
                        <Bar 
                            data={maritalChartData} 
                            options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                plugins: { legend: { position: 'top' } }
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Recent Registrations */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <h2 className="text-lg font-semibold text-gray-800 p-6 pb-0">Recent Registrations</h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {stats.recentUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm text-gray-900">{user.id}</td>
                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{user.name}</td>
                                    <td className="px-6 py-4 text-sm text-gray-600">{user.gender}</td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {new Date(user.created).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                            user.pay_status === 'paid' 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {user.pay_status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;